import { LOCATIONS } from "../../common/location";

const resolveLocationDisplay = (intakeQuestions, patientLocation) => {
    if (intakeQuestions?.location?.trim()) {
        return intakeQuestions.location.trim();
    }
    if (patientLocation) {
        const entry = Object.entries(LOCATIONS).find(([, code]) => code === patientLocation);
        return entry?.[0] || "";
    }
    return "";
};

const formatTelephoneFromIntake = (callerNumber, callerNumberExt) => {
    const number = callerNumber?.trim();
    if (!number) return "";
    const ext = callerNumberExt?.trim();
    return ext ? `${number} ext ${ext}` : number;
};

const setFreeTextByTag = (questionResponse, tag, value) => {
    if (!value?.trim()) return questionResponse;

    return questionResponse.map((question) => {
        if (question.tag !== tag) return question;

        const answer = [...(question.answer || [])];
        if (answer[0]) {
            answer[0] = { ...answer[0], answer: value.trim() };
        }

        return { ...question, answer };
    });
};

const setRadioByTag = (questionResponse, tag, selectedOption) => {
    if (!selectedOption) return questionResponse;

    return questionResponse.map((question) => {
        if (question.tag !== tag) return question;

        return {
            ...question,
            answer: question.answer.map((opt) => ({
                ...opt,
                answer: opt.option === selectedOption,
            })),
        };
    });
};

const setDropDownByTag = (questionResponse, tag, value) => {
    if (!value?.trim()) return questionResponse;

    const trimmed = value.trim();

    return questionResponse.map((question) => {
        if (question.tag !== tag) return question;

        let answer = question.answer.map((opt) => ({
            ...opt,
            answer:
                trimmed === opt.value || trimmed === opt.option || trimmed === opt.name
                    ? trimmed
                    : "",
        }));

        const selected = answer.filter((item) => item.answer === trimmed);
        const rest = answer.filter((item) => item.answer !== trimmed);
        answer = [...selected, ...rest];

        return { ...question, answer };
    });
};

const TEXT_ANSWER_TYPES = new Set(["freeText", "singleFreeText"]);
const CHOICE_ANSWER_TYPES = new Set(["radio", "checkbox"]);

const getQuestionType = (question) =>
    question?.answerType || question?.questionType || null;

const typesCompatible = (destType, sourceType) => {
    if (!destType || !sourceType) return false;
    if (destType === sourceType) return true;
    return TEXT_ANSWER_TYPES.has(destType) && TEXT_ANSWER_TYPES.has(sourceType);
};

const getSourceTextAnswer = (sourceItem) => {
    const answers = sourceItem?.answer || [];
    const withText = answers.find((a) =>
        String(a?.valueString ?? a?.answer ?? "").trim()
    );
    if (!withText) return "";
    return String(withText.valueString ?? withText.answer).trim();
};

const getSourceSelectedOptions = (sourceItem) =>
    (sourceItem?.answer || [])
        .filter((a) => a?.valueBoolean === true || a?.answer === true)
        .map((a) => a.option)
        .filter(Boolean);

const applyTextAnswer = (destQuestion, value) => {
    if (!value) return destQuestion;

    const answer = [...(destQuestion.answer || [])];
    if (answer[0]) {
        answer[0] = { ...answer[0], answer: value };
    } else {
        answer.push({ answer: value });
    }

    return { ...destQuestion, answer };
};

const applyChoiceAnswer = (destQuestion, selectedOptions) => {
    if (!selectedOptions?.length || !destQuestion.answer?.length) return destQuestion;

    const destOptions = destQuestion.answer.map((opt) => opt.option);
    if (!selectedOptions.some((opt) => destOptions.includes(opt))) return destQuestion;

    const selectedSet = new Set(selectedOptions);
    return {
        ...destQuestion,
        answer: destQuestion.answer.map((opt) => ({
            ...opt,
            answer: selectedSet.has(opt.option),
        })),
    };
};

const applyDropDownAnswer = (destQuestion, value) => {
    if (!value || !destQuestion.answer?.length) return destQuestion;

    const trimmed = value.trim();
    const matchesOption = (opt) =>
        trimmed === opt.value || trimmed === opt.option || trimmed === opt.name;

    if (!destQuestion.answer.some(matchesOption)) return destQuestion;

    let answer = destQuestion.answer.map((opt) => ({
        ...opt,
        answer: matchesOption(opt) ? trimmed : "",
    }));
    const selected = answer.filter((item) => item.answer === trimmed);
    const rest = answer.filter((item) => item.answer !== trimmed);
    return { ...destQuestion, answer: [...selected, ...rest] };
};

/**
 * Copies a source QuestionnaireResponse item's answer onto one destination
 * question. Returns destQuestion unchanged when types or options do not line up,
 * or when the source has no answer.
 */
export const applySourceAnswerToQuestion = (destQuestion, sourceItem) => {
    if (!destQuestion || !sourceItem) return destQuestion;

    const destType = getQuestionType(destQuestion);
    const sourceType = getQuestionType(sourceItem);
    if (!typesCompatible(destType, sourceType)) return destQuestion;

    if (TEXT_ANSWER_TYPES.has(destType)) {
        return applyTextAnswer(destQuestion, getSourceTextAnswer(sourceItem));
    }

    if (destType === "dropDown") {
        return applyDropDownAnswer(destQuestion, getSourceTextAnswer(sourceItem));
    }

    if (CHOICE_ANSWER_TYPES.has(destType)) {
        return applyChoiceAnswer(destQuestion, getSourceSelectedOptions(sourceItem));
    }

    return destQuestion;
};

const normalizeQuestionText = (item) =>
    (item?.text || item?.question || "").trim().toLowerCase();

const getItemQuestionId = (item) => item?.questionId || item?.id || null;

const isQuestionAnswered = (question) => {
    const type = getQuestionType(question);
    const answers = question?.answer || [];

    if (TEXT_ANSWER_TYPES.has(type) || type === "dropDown") {
        return answers.some((a) => String(a?.answer ?? "").trim().length > 0);
    }

    if (CHOICE_ANSWER_TYPES.has(type)) {
        return answers.some((a) => a?.answer === true);
    }

    return answers.some(
        (a) => a?.answer === true || String(a?.answer ?? "").trim().length > 0
    );
};

const findSourceItem = (destQuestion, sourceItems) => {
    const destId = getItemQuestionId(destQuestion);
    if (destId) {
        const byId = sourceItems.find((item) => getItemQuestionId(item) === destId);
        if (byId) return byId;
    }

    const destText = normalizeQuestionText(destQuestion);
    if (!destText) return null;

    return sourceItems.find((item) => normalizeQuestionText(item) === destText) || null;
};

const applySourceItemsToQuestionResponse = (questionResponse, sourceItems) => {
    if (!sourceItems?.length) {
        return { questionResponse, didPrefill: false };
    }

    let didPrefill = false;
    const result = questionResponse.map((question) => {
        if (isQuestionAnswered(question)) return question;

        const sourceItem = findSourceItem(question, sourceItems);
        if (!sourceItem) return question;

        const filled = applySourceAnswerToQuestion(question, sourceItem);
        if (filled !== question) {
            didPrefill = true;
        }
        return filled;
    });

    return { questionResponse: result, didPrefill };
};

/**
 * Prefills questionnaire template from encounter intakeQuestions (reverse of buildWaitingRoomIntakeQuestions),
 * then fills remaining unanswered questions from the source QuestionnaireResponse items.
 */
export const applyIntakeQuestionsToQuestionResponse = (
    template,
    intakeQuestions,
    { patientLocation, sourceItems } = {}
) => {
    if (!template?.length) {
        return { questionResponse: template || [], didPrefill: false };
    }

    let result = [...template];
    let didPrefill = false;

    if (intakeQuestions) {
        if (intakeQuestions.callerName?.trim()) {
            result = setFreeTextByTag(result, "CALLER", intakeQuestions.callerName);
            didPrefill = true;
        }

        const telephone = formatTelephoneFromIntake(
            intakeQuestions.callerNumber,
            intakeQuestions.callerNumberExt
        );
        if (telephone) {
            result = setFreeTextByTag(result, "TELEPHONE", telephone);
            didPrefill = true;
        }

        if (intakeQuestions.intakeReason?.trim()) {
            result = setFreeTextByTag(result, "REASON", intakeQuestions.intakeReason);
            didPrefill = true;
        }

        const relationship = intakeQuestions.callerRelationship?.trim();
        if (relationship === "Self") {
            result = setRadioByTag(result, "SELFCALLING", "Yes");
            didPrefill = true;
        } else if (relationship) {
            result = setRadioByTag(result, "SELFCALLING", "No");
            result = setDropDownByTag(result, "RELATIONSHIP", relationship);
            didPrefill = true;
        }

        const locationDisplay = resolveLocationDisplay(intakeQuestions, patientLocation);
        if (locationDisplay) {
            result = setDropDownByTag(result, "LOCATION", locationDisplay);
            didPrefill = true;
        }
    }

    const fromSource = applySourceItemsToQuestionResponse(result, sourceItems);
    return {
        questionResponse: fromSource.questionResponse,
        didPrefill: didPrefill || fromSource.didPrefill,
    };
};
